import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useIAPList } from '@/modules/iap/model/list';
import { ROUTE_NAME_PROMOTION } from '@/modules/promotion/router';
import { useRfmGroupList } from '@/modules/rfm/group/model/list';
import { useRfmPersonaList } from '@/modules/rfm/persona/model/list';
import { promotionService } from '@/services/promotion';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';

export function usePromotionPrototypeAdd() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { rfmGroupOptions, fetchRfmGroupAll } = useRfmGroupList();
    const { personaAll, fetchPersonaAll } = useRfmPersonaList();
    const { iapOptions, list: ipaAll, fetchList: fetchIapAll } = useIAPList();

    const model = reactive({
        id: null,
        rfmGroupId: null,
        name: '',
        list: []
    });

    const validations = {
        name: { required },
        rfmGroupId: { required }
    };

    const validateModel = computed(() => {
        if (StringUtils.isEmpty(model.name)) {
            return false;
        }
        if (model.list.filter(i => !i.canAdd).length > 0) {
            return false;
        }

        return true;
    });

    const v$ = useVuelidate(validations, model);

    const beforeFetch = async () => {
        await fetchRfmGroupAll();
        await fetchIapAll();
        await fetchPersonaAll();
    };

    const fetch = async () => {
        if (!model.rfmGroupId) {
            return;
        }

        model.list = [];
        const personaAllByGroup = personaAll.value.filter(item => item.groupId == model.rfmGroupId);

        for (let i = 0; i < personaAllByGroup.length; i++) {
            const data = {
                index: i,
                personaId: personaAllByGroup[i].id,
                personaName: personaAllByGroup[i].name,
                iapId: 0,
                packageId: '',
                price: 0,
                credit: 0,
                rate: 0,
                canAdd: false
            };

            model.list.push(data);
        }

        // clone case
        if (route.params.id) {
            crudAction.executeAction({
                request: () => promotionService.prototype.get(route.params.id),
                onSuccess: ({ data }) => {
                    model.id = route.params.id;
                    model.list.forEach((item: any) => {
                        const prototypeItem = data.find((proto: any) => proto.rfmPersonaId == item.personaId);

                        const iapItem = prototypeItem
                            ? ipaAll.value.find(
                                  (iap: any) =>
                                      prototypeItem.packageId == iap.packageId &&
                                      prototypeItem.price == iap.price &&
                                      prototypeItem.credit == iap.credit
                              )
                            : null;

                        if (prototypeItem && iapItem) {
                            item.iapId = iapItem.id;
                            item.packageId = prototypeItem.packageId;
                            item.price = prototypeItem.price;
                            item.rate = prototypeItem.rate;
                            item.credit = prototypeItem.credit;
                            actions.validateItem(item);
                        }
                    });
                },
                onError: () => router.push({ name: ROUTE_NAME_PROMOTION.PROTOTYPE.INDEX })
            });
        }
    };

    const actions = {
        create: crudAction.excuteAndNotify({
            canExecute: () => validateModel.value,
            validator: v$,
            request: () => {
                const personaAll: any[] = [];
                const packageList: any[] = [];
                const priceList: any[] = [];
                const creditList: any[] = [];
                const rateList: any[] = [];

                model.list.forEach(item => {
                    const index = item.index;
                    if (item.canAdd) {
                        personaAll[index] = item.personaId;
                        packageList[index] = item.packageId;
                        priceList[index] = item.price;
                        creditList[index] = item.credit;
                        rateList[index] = item.rate;
                    }
                });

                return promotionService.prototype.create(
                    model.name,
                    personaAll.toString(),
                    packageList.toString(),
                    priceList.toString(),
                    creditList.toString(),
                    rateList.toString()
                );
            },
            onSuccess: () => router.push({ name: ROUTE_NAME_PROMOTION.PROTOTYPE.INDEX })
        }),

        validateItem: (subChangeset: any) => {
            subChangeset.canAdd = !StringUtils.isEmpty(subChangeset.packageId) && Number(subChangeset.rate) > 100;
        },
        selectIAP: (iapId: any, subChangeset: any) => {
            ipaAll.value.forEach(item => {
                if (item.id == iapId) {
                    subChangeset.packageId = item.packageId;
                    subChangeset.price = item.price;
                    subChangeset.credit = item.credit;
                }
            });

            actions.validateItem(subChangeset);
        },
        changeRate: (subChangeset: any) => {
            actions.validateItem(subChangeset);
        },
        clearRow: (subChangeset: any) => {
            subChangeset.iapId = 0;
            subChangeset.packageId = '';
            subChangeset.price = 0;
            subChangeset.credit = 0;
            subChangeset.rate = 0;
            subChangeset.canAdd = false;
        }
    };

    return { model, v$, validateModel, fetch, beforeFetch, rfmGroupOptions, iapOptions, actions };
}
